/**
 * WIDGET DE OVERLAY — `/{slug}/widget/court/{matchId}`, browser source de OBS.
 *
 * ## Éste SÍ es SSE, y por qué (ADR-020)
 *
 * Es el único consumidor del portal público donde el streaming gana: hay UNO
 * por transmisión (no miles), está enchufado a la corriente, y un marcador que
 * aparece cinco segundos tarde arriba del video se nota. La pantalla del
 * espectador (`PublicMatch`) hace lo contrario —snapshot cacheado + polling—
 * porque ahí el problema es la escala, no la latencia.
 *
 * ## Doble transporte a propósito: SSE arriba de polling
 *
 * El widget hace las dos cosas al mismo tiempo, y no es redundancia inútil:
 *
 *  - **El snapshot pinta el marcador de entrada.** Cuando el productor arrastra
 *    el browser source a la escena, el tablero aparece con el 2-1 y 18-14 sin
 *    esperar a que alguien haga un punto.
 *  - **El SSE lo mantiene al instante** mientras esté conectado.
 *  - **Si el SSE no está** —porque el server contestó 503 por el tope de
 *    fan-out (`SCOUT_PUBLIC_SSE_MAX`), o porque un proxy corta los streams— el
 *    polling sigue solo y el overlay funciona igual, con 5 segundos de retraso.
 *    Degradado, no roto: en el aire, un marcador con retraso es infinitamente
 *    mejor que un marcador que desapareció.
 *
 * Con el stream abierto, el polling baja a red de seguridad (30 s) para no
 * pedir de más.
 *
 * ## Opciones por query (las tipea el productor en OBS)
 *
 *   ?scale=1.4     tamaño del panel (0.5 – 4)
 *   ?position=…    top-left | top-right | bottom-left | bottom-right
 *   ?partials=0    esconde la columna de parciales (para overlays angostos)
 *
 * ## Fondo transparente
 *
 * El body del widget NO pinta fondo: lo que se ve detrás es el video. El panel
 * lleva su propio fondo opaco. `CourtWidget` pone la clase en `<body>` al
 * montar y la saca al desmontar, para que el resto del portal siga con su tema.
 */
import { useEffect, useState } from 'react';
import { useT } from '../../i18n/use-t';
import { LiveSubscription, type LiveStatus } from '../../lib/live';
import type {
  PublicClient,
  PublicMatchResponse,
  PublicScoreboard,
  PublicSetLine,
} from '../../lib/public-client';
import { usePolled } from './hooks';

/** Cada cuánto pollea el widget cuando el stream ESTÁ abierto (red de seguridad). */
const SAFETY_POLL_MS = 30_000;
/** Sin novedades por más de esto, se avisa en pantalla que el dato está viejo. */
const STALE_AFTER_MS = 25_000;

export function CourtWidget({
  client,
  slug,
  matchId,
  query,
}: {
  client: PublicClient;
  slug: string;
  matchId: string;
  query: Record<string, string>;
}): JSX.Element {
  const t = useT();
  const [live, setLive] = useState<PublicScoreboard | null>(null);
  const [status, setStatus] = useState<LiveStatus>('idle');
  const [liveAt, setLiveAt] = useState<number | null>(null);
  const streamOpen = status === 'open';

  // Fondo transparente sólo mientras el widget está montado: el resto del
  // portal conserva su tema. La clase va en `<body>` porque el fondo lo pinta
  // `styles.css` ahí, y lo que se ve detrás del overlay es el video de la
  // transmisión, no una página.
  useEffect(() => {
    document.body.classList.add('pub-widget-body');
    return () => document.body.classList.remove('pub-widget-body');
  }, []);

  const polled = usePolled<PublicMatchResponse>(
    () => client.getMatch(slug, matchId),
    [slug, matchId],
    streamOpen ? SAFETY_POLL_MS : undefined,
  );

  // --- SSE -----------------------------------------------------------------
  const url = client.liveUrl(slug, matchId);
  useEffect(() => {
    const sub = new LiveSubscription({
      url,
      // Sin headers de autorización: es una URL pública y el widget corre en
      // un browser source de OBS, donde no hay sesión de nadie.
      onStatus: (next) => setStatus(next),
      onMessage: (message) => {
        if (message.event !== 'score') return;
        try {
          setLive(JSON.parse(message.data) as PublicScoreboard);
          setLiveAt(Date.now());
        } catch {
          // Un mensaje roto no tira el overlay: se queda el marcador anterior.
        }
      },
    });
    sub.start();
    // `stop()` es definitivo (aborta el fetch en curso y no reintenta): con el
    // browser source cerrado no puede quedar un stream contando contra el tope
    // de `SCOUT_PUBLIC_SSE_MAX`.
    return () => sub.stop();
  }, [url]);

  // El marcador que se dibuja: el del stream si llegó, y si no el del snapshot.
  const board = live ?? boardFromSnapshot(polled.data);
  const freshAt = liveAt ?? polled.updatedAt;
  const stale = freshAt !== null && Date.now() - freshAt > STALE_AFTER_MS;

  return <CourtPanel board={board} query={query} stale={stale} />;
}

/**
 * El panel en sí, sin transporte: recibe un tablero y lo dibuja.
 *
 * Se exporta para poder renderizarlo con datos a mano en los tests
 * (`test/public-pages.test.tsx`), que es la única forma de verificar el
 * overlay sin OBS: acá no hay nada que se pueda probar con un click.
 */
export function CourtPanel({
  board,
  query,
  stale = false,
}: {
  board: PublicScoreboard | null;
  query: Record<string, string>;
  stale?: boolean;
}): JSX.Element {
  const t = useT();
  const scale = clamp(Number(query.scale ?? '1'), 0.5, 4, 1);
  const position = query.position ?? 'top-left';
  const showPartials = query.partials !== '0';

  if (board === null) {
    // Nada todavía: NO se dibuja un panel vacío. Un overlay con un "0-0" falso
    // arriba del video en vivo es peor que un overlay que todavía no apareció.
    return <div className="pub-widget" aria-hidden />;
  }

  return (
    <div
      className="pub-widget"
      style={{
        fontSize: `${scale}em`,
        justifyContent: position.endsWith('right') ? 'flex-end' : 'flex-start',
        alignItems: position.startsWith('bottom') ? 'flex-end' : 'flex-start',
        minHeight: '100vh',
        flexDirection: 'column',
        padding: '2vh 2vw',
      }}
    >
      <div className="pub-widget-panel" role="img" aria-label={ariaLabel(board, t)}>
        <div className="pub-widget-mark" />
        <div className="pub-widget-teams">
          <TeamLine
            name={board.home.name}
            sets={board.home.setsWon}
            score={board.home.score}
            serving={board.serving === 'home'}
          />
          <TeamLine
            name={board.away.name}
            sets={board.away.setsWon}
            score={board.away.score}
            serving={board.serving === 'away'}
          />
        </div>
        {showPartials && board.sets.length > 0 ? (
          <div className="pub-widget-partials">
            {board.sets.map((set) => (
              <span key={set.set}>
                {set.home}-{set.away}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {stale ? <div className="pub-widget-stale">{t('publicPortal.widgetStale')}</div> : null}
    </div>
  );
}

function TeamLine({
  name,
  sets,
  score,
  serving,
}: {
  name: string;
  sets: number;
  score: number;
  serving: boolean;
}): JSX.Element {
  return (
    <div className="pub-widget-team">
      <span className="pub-widget-name">
        {serving ? <span className="pub-widget-serve" /> : null}
        {name}
      </span>
      <span className="pub-widget-sets">{sets}</span>
      <span className="pub-widget-score">{score}</span>
    </div>
  );
}

/**
 * Snapshot completo → tablero. Es la MISMA proyección que hace el API para el
 * stream (`toScoreboard`), replicada acá sólo para el camino degradado: si el
 * SSE no está, el widget arma el tablero del documento que sí bajó.
 */
function boardFromSnapshot(data: PublicMatchResponse | null): PublicScoreboard | null {
  const match = data?.match;
  if (!match) return null;
  return {
    matchId: match.matchId,
    status: match.status,
    home: { name: match.home.name, setsWon: match.home.setsWon, score: match.current?.home ?? 0 },
    away: { name: match.away.name, setsWon: match.away.setsWon, score: match.current?.away ?? 0 },
    set: match.current?.set ?? match.sets.length,
    serving: match.current?.serving ?? null,
    sets: match.sets as PublicSetLine[],
    lastSeq: match.lastSeq,
  };
}

function clamp(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

/**
 * El overlay es una imagen para un lector de pantalla: se le da el marcador en
 * una línea. No es decorativo —es el dato— así que `aria-hidden` sería peor.
 */
function ariaLabel(board: PublicScoreboard, t: ReturnType<typeof useT>): string {
  return t('publicPortal.widgetLabel', {
    home: board.home.name,
    away: board.away.name,
    setsHome: board.home.setsWon,
    setsAway: board.away.setsWon,
    scoreHome: board.home.score,
    scoreAway: board.away.score,
  });
}
